import http from 'helpers/requests';
import { SupplierApiError, mapSupplierApiError, supplierRequestAndParse } from './supplierApiCore';
import {
  supplierSourceUploadDetailResponseV1Schema,
  supplierSourceUploadListResponseV1Schema,
} from './supplierUploadContracts';

export const SUPPLIER_UPLOAD_ENDPOINTS = Object.freeze({
  create: '/supplier/uploads',
  list: '/supplier/uploads',
  get: (uploadId) => `/supplier/uploads/${uploadId}`,
});

export class SupplierUploadApiError extends SupplierApiError {
  constructor(payload, httpStatus) {
    // The rejection envelope's message lives at `payload.error.message`
    // (supplierSourceUploadRejectedResponseV1Schema) — prefer it over a
    // bare `payload.message`.
    super(
      payload,
      httpStatus,
      'Supplier upload request failed',
      (p) => p?.error?.message || p?.message,
    );
    this.name = 'SupplierUploadApiError';
  }
}

const mapApiError = (error) => mapSupplierApiError(error, SupplierUploadApiError);

// Tells the backend this client understands Template Issues: the rejection
// codes REPORTING_PERIOD_REQUIRED / TEMPLATE_DATA_INVALID, `issues` on a
// rejection and `warnings` on a 201. Without it the backend answers in the
// pre-issue shape (PARSE_FAILED, no issues), which is what kept older builds of
// this strict mirror parsing. Sent on every upload request, reads included —
// a rejected upload is listed, so the list needs the same contract.
const ISSUE_CONTRACT = { headers: { 'X-Supplier-Upload-Contract': '2' } };

// POST /supplier/uploads is SYNCHRONOUS — ingestion (quarantine → hostile-file
// boundary → parse → validate → draft creation) all happens inside this one
// request/response cycle. There is no polling: 201 already carries the drafts;
// 400/422/429 carry the rejection envelope (supplierSourceUploadRejectedResponseV1).
//
// installationId is OPTIONAL (S10 backend amendment): the caller omits it
// (CommsTemplateDropzone's "Detect from template" default) to let the
// backend infer the installation from the template itself — only append it
// to the FormData when the caller resolved an explicit id, so an omitted
// value is genuinely absent from the request rather than serialized as the
// literal string "undefined".
//
// reportingPeriod is OPTIONAL: sent only on the retry that follows a
// REPORTING_PERIOD_REQUIRED rejection, as two flat multipart fields.
export const createSupplierUpload = ({ file, installationId, reportingPeriod }) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('version', '1');
  if (installationId) {
    formData.append('installationId', installationId);
  }
  if (reportingPeriod?.from && reportingPeriod?.to) {
    formData.append('reportingPeriodFrom', reportingPeriod.from);
    formData.append('reportingPeriodTo', reportingPeriod.to);
  }
  return supplierRequestAndParse(
    () => http.post(SUPPLIER_UPLOAD_ENDPOINTS.create, formData, ISSUE_CONTRACT),
    supplierSourceUploadDetailResponseV1Schema,
    mapApiError,
  );
};

export const fetchSupplierUploads = () =>
  supplierRequestAndParse(
    () => http.get(SUPPLIER_UPLOAD_ENDPOINTS.list, ISSUE_CONTRACT),
    supplierSourceUploadListResponseV1Schema,
    mapApiError,
  );

export const fetchSupplierUpload = (uploadId) =>
  supplierRequestAndParse(
    () => http.get(SUPPLIER_UPLOAD_ENDPOINTS.get(uploadId), ISSUE_CONTRACT),
    supplierSourceUploadDetailResponseV1Schema,
    mapApiError,
  );
